/**
 * Runtime API configuration loaded from /config/* endpoints.
 *
 * Nginx (or webpack-dev-server in local watch) serves the gui-config ConfigMap
 * keys at /config/<key>. This module loads them at startup and resolves a base URL
 * per service according to the `routing` mode:
 *
 *   subdomain - https://<apiRevision>-<service>-<namespace>.<domain>   (Knative-per-revision, feature env)
 *   path      - <basePath>                                             (envoy path routing, prod / local)
 *
 * If config cannot be loaded, getServiceUrl() returns ''. Callers must therefore
 * ensure the configmap is deployed in every environment (feature, prod, localdev).
 */

type RoutingMode = 'subdomain' | 'path';

interface ServiceConfig {
  serviceName: string;
  domain: string;
  apiRevision: string;
  basePath: string;
}

interface ApiConfig {
  routing: RoutingMode;
  namespace: string;
  domain: string;
  natsUrl: string;
  services: Map<string, ServiceConfig>;
  loaded: boolean;
}

export const apiConfig: ApiConfig = {
  routing: 'subdomain',
  namespace: '',
  domain: '',
  natsUrl: '',
  services: new Map(),
  loaded: false,
};

async function fetchConfigKey(key: string): Promise<string> {
  try {
    const res = await fetch(`/config/${key}`);
    return res.ok ? (await res.text()).trim() : '';
  } catch {
    return '';
  }
}

function buildServiceUrl(svc: ServiceConfig, namespace: string, routing: RoutingMode): string {
  if (routing === 'path') {
    return svc.basePath;
  }
  const host = svc.apiRevision
    ? `${svc.apiRevision}-${svc.serviceName}-${namespace}.${svc.domain}`
    : `${svc.serviceName}-${namespace}.${svc.domain}`;
  return `https://${host}`;
}

export async function loadApiConfig(): Promise<void> {
  if (apiConfig.loaded) {
    return;
  }

  const [routing, namespace, domain, servicesCsv, natsUrl] = await Promise.all([
    fetchConfigKey('routing'),
    fetchConfigKey('namespace'),
    fetchConfigKey('domain'),
    fetchConfigKey('services'),
    fetchConfigKey('nats.url'),
  ]);

  if (!servicesCsv) {
    apiConfig.loaded = true;
    return;
  }

  apiConfig.routing = routing === 'path' ? 'path' : 'subdomain';
  apiConfig.namespace = namespace;
  apiConfig.domain = domain;
  apiConfig.natsUrl = natsUrl;

  const serviceNames = servicesCsv.split(',').map(s => s.trim()).filter(Boolean);

  await Promise.all(
    serviceNames.map(async (name) => {
      const [svcDomain, apiRevision, basePath] = await Promise.all([
        fetchConfigKey(`service.${name}.domain`),
        fetchConfigKey(`service.${name}.apiRevision`),
        fetchConfigKey(`service.${name}.basePath`),
      ]);
      apiConfig.services.set(name, {
        serviceName: name,
        domain: svcDomain || domain,
        apiRevision,
        basePath,
      });
    })
  );

  apiConfig.loaded = true;
}

const serviceAliases: Record<string, string> = {
  'inboxv2-api': 'inbox-minapi',
};

export function getServiceUrl(serviceName: string): string {
  const resolvedName = serviceAliases[serviceName] || serviceName;
  const svc = apiConfig.services.get(resolvedName);
  if (!svc) return '';
  if (apiConfig.routing === 'path') {
    return svc.basePath;
  }
  if (apiConfig.namespace) {
    return buildServiceUrl(svc, apiConfig.namespace, apiConfig.routing);
  }
  return '';
}

/**
 * Get the NATS leaf websocket URL.
 *
 * @param env - Environment name from the NATS token (e.g., 'production', 'staging-dev', 'test')
 * @returns WSS URL for the NATS leaf node
 */
export function getNatsUrl(env: string): string {
  if (apiConfig.natsUrl) {
    return apiConfig.natsUrl;
  }
  if (env === 'production') {
    return 'wss://insight.dynamik.no/leaf';
  }
  return `wss://${env.replace('-dev', '')}.env.insight.dynamik.no/leaf`;
}
